// ANALYSIS AND CHANGES:
// This is a new custom hook that centralizes the position management logic.
// RESPONSABILIDADES:
// 1. Manage the state of the Add and Edit position modals (`isAddEditModalOpen`).
// 2. Track the position being edited (`editingPosition`).
// 3. Track the ID of the position to delete for the confirmation modal (`positionToDelete`).
// 4. Encapsulate the API calls (mutations) to add, update and delete positions.
// 5. Expose clean handlers (`handleOpenAddModal`, `handleSavePosition`, etc.) for the UI component to use.
// BENEFITS:
// - Moves the complex business logic out of `CareersPage`, keeping it cleaner and presentation-focused.
// - Makes the position management logic easier to reuse and test in isolation.

import { useState } from "react";
import { useJobPositions } from "@hooks/useJobPositions";
import { Position } from "@common";
import { message } from "antd";

export const useJobPositionsManager = () => {
  const { addJobPosition, updateJobPosition, softDeleteJobPosition } = useJobPositions();
  
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [positionToDelete, setPositionToDelete] = useState<string | null>(null);

  const handleOpenAddModal = () => {
    setEditingPosition(null);
    setIsAddEditModalOpen(true);
  };

  const handleOpenEditModal = (position: Position) => {
    setEditingPosition(position);
    setIsAddEditModalOpen(true);
  };

  const handleCloseAndResetModal = () => {
    setIsAddEditModalOpen(false);
    setEditingPosition(null);
  };

  const handleSetPositionToDelete = (id: string | null) => {
    setPositionToDelete(id);
  };

  const handleSavePosition = (positionData: Position) => {
    const onSaveSuccess = (successMessage: string) => {
      message.success(successMessage);
      handleCloseAndResetModal();
    };
    
    const onSaveError = (errorMessage: string) => {
      message.error(errorMessage);
    };

    if (editingPosition) {
      // Update
      updateJobPosition.mutate(
        { id: editingPosition.id, data: positionData },
        {
          onSuccess: () => onSaveSuccess("Position updated successfully!"),
          onError: () => onSaveError("Failed to update position."),
        }
      );
    } else {
      // Add
      addJobPosition.mutate(positionData, {
        onSuccess: () => onSaveSuccess("Position added successfully!"),
        onError: () => onSaveError("Failed to add position."),
      });
    }
  };

  const handleConfirmDelete = () => {
    if (!positionToDelete) return;

    softDeleteJobPosition.mutate(positionToDelete, {
      onSuccess: () => {
        message.success("Position removed successfully!");
        setPositionToDelete(null);
      },
      onError: () => message.error("Failed to remove position."),
    });
  };

  return {
    isAddEditModalOpen,
    editingPosition,
    positionToDelete,
    handleOpenAddModal,
    handleOpenEditModal,
    handleCloseAndResetModal,
    handleSetPositionToDelete,
    handleConfirmDelete,
    handleSavePosition,
    isLoading: addJobPosition.isPending || updateJobPosition.isPending || softDeleteJobPosition.isPending,
  };
};
